import { ChangeDetectionStrategy, Component, effect, input, output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TextInputComponent } from '@examdojo/core/input';
import { IconComponent } from '@examdojo/core/icon';
import type { ActionDefinition } from '../../../../model/widget-action.model';
import type { ReactionBranch, ReactionGroup } from '../../../../model/reaction.model';
import { TriggerEditorComponent } from './trigger-editor.component';

@Component({
  selector: 'dojo-reaction-group-editor',
  standalone: true,
  imports: [ReactiveFormsModule, MatSlideToggleModule, TextInputComponent, IconComponent, TriggerEditorComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-2">
      @for (branch of reactionGroup().match; track $index) {
        <div class="rounded-md border border-neutral-100 bg-white p-2">
          <div class="mb-1.5 flex items-center gap-1">
            @if (branch.else) {
              <span class="text-[10px] font-medium text-neutral-500">Else (default)</span>
            } @else {
              <dojo-text-input
                label="When"
                [formCtrl]="getWhenCtrl($index)"
                [size]="'s'"
                placeholder="f.a > 0"
                class="flex-1"
              />
            }
            <div class="flex items-center gap-1">
              @if (!branch.else) {
                <mat-slide-toggle class="scale-75" [checked]="false" (change)="onToggleElse($index, $event.checked)"
                  >Else</mat-slide-toggle
                >
              } @else {
                <mat-slide-toggle class="scale-75" [checked]="true" (change)="onToggleElse($index, $event.checked)"
                  >Else</mat-slide-toggle
                >
              }
              <dojo-icon
                icon="xmark"
                [size]="10"
                class="cursor-pointer text-neutral-400 hover:text-red-500"
                (click)="onRemoveBranch($index)"
              />
            </div>
          </div>

          <dojo-trigger-editor
            [trigger]="getBranchDispatch(branch)"
            [elementIds]="elementIds()"
            (triggerChange)="onBranchDispatchChange($index, $event)"
          />
        </div>
      }

      <button
        class="rounded-md border border-dashed border-neutral-200 px-2 py-1 text-[11px] text-neutral-400 hover:border-sky-300 hover:text-sky-500"
        (click)="onAddBranch()"
      >
        + Add Branch
      </button>
    </div>
  `,
})
export class ReactionGroupEditorComponent {
  constructor() {
    effect(() => {
      this.reactionGroup();
      this.syncWhenCtrls();
    });
  }

  readonly reactionGroup = input.required<ReactionGroup>();
  readonly elementIds = input<string[]>([]);
  readonly reactionGroupChange = output<ReactionGroup>();

  private readonly whenCtrls = new Map<number, FormControl<string>>();
  private readonly whenSubs = new Set<number>();

  getWhenCtrl(index: number): FormControl<string> {
    if (!this.whenCtrls.has(index)) {
      const ctrl = new FormControl<string>('', { nonNullable: true });
      this.whenCtrls.set(index, ctrl);
    }
    if (!this.whenSubs.has(index)) {
      this.whenSubs.add(index);
      const ctrl = this.whenCtrls.get(index)!;
      // Subscribe to value changes — we can't use takeUntilDestroyed in a getter,
      // but forms will be garbage collected with the component
      // eslint-disable-next-line rxjs/no-subscribe-handlers
      ctrl.valueChanges.subscribe((val) => {
        this.onWhenChange(index, val);
      });
    }
    return this.whenCtrls.get(index)!;
  }

  getBranchDispatch(branch: ReactionBranch): ActionDefinition {
    const dispatch = branch.dispatch;
    if (Array.isArray(dispatch)) {
      return dispatch[0] ?? { action: 'PATCH_DISPLAY' };
    }
    return dispatch;
  }

  onWhenChange(index: number, when: string): void {
    const group = this.reactionGroup();
    const branches = [...group.match];
    const branch = branches[index];
    branches[index] = { ...branch, when: when || undefined };
    this.reactionGroupChange.emit({ ...group, match: branches });
  }

  onToggleElse(index: number, isElse: boolean): void {
    const group = this.reactionGroup();
    const branches = [...group.match];
    const branch = branches[index];
    if (isElse) {
      const { when: _, ...rest } = branch;
      branches[index] = { ...rest, else: true };
    } else {
      const { else: _, ...rest } = branch;
      branches[index] = { ...rest, when: '' };
    }
    this.reactionGroupChange.emit({ ...group, match: branches });
  }

  onBranchDispatchChange(index: number, dispatch: ActionDefinition): void {
    const group = this.reactionGroup();
    const branches = [...group.match];
    const branch = branches[index];
    branches[index] = { ...branch, dispatch };
    this.reactionGroupChange.emit({ ...group, match: branches });
  }

  onRemoveBranch(index: number): void {
    const group = this.reactionGroup();
    const branches = [...group.match];
    branches.splice(index, 1);
    this.whenCtrls.delete(index);
    this.whenSubs.delete(index);
    this.reactionGroupChange.emit({ ...group, match: branches });
  }

  onAddBranch(): void {
    const group = this.reactionGroup();
    const newBranch: ReactionBranch = {
      when: '',
      dispatch: { action: 'PATCH_DISPLAY', payload: {} },
    };
    this.reactionGroupChange.emit({ ...group, match: [...group.match, newBranch] });
  }

  private syncWhenCtrls(): void {
    const branches = this.reactionGroup().match;
    for (let i = 0; i < branches.length; i++) {
      const branch = branches[i];
      if (!branch.else) {
        const ctrl = this.getWhenCtrl(i);
        ctrl.setValue(branch.when ?? '', { emitEvent: false });
      }
    }
  }
}
